import { useEffect, useRef, useState } from 'react'
import type { ReactElement } from 'react'
import { MAX_CHAT_LENGTH } from '@games/protocol'
import type { ChatEntry } from '../stores/chat'
import { ScrollArea } from './ScrollArea'

export interface ChatPanelProps {
  readonly messages: readonly ChatEntry[]
  readonly disabled?: boolean
  /**
   * 快捷台词。由**具体游戏**经注册表提供 —— 通用层不得内嵌任何游戏的文案；
   * 空数组时不渲染入口按钮。
   */
  readonly taunts?: readonly string[]
  readonly onSend: (text: string) => void
}

/**
 * 纯聊天内容：消息列表 + 输入行。**不管摆放位置、不管可见性** ——
 * 「桌面右侧固定栏」与「移动端全屏覆盖层」都由 `ChatDock` 决定。
 * 列表用 flex-1 撑满容器高度，因此不需要任何 max-height 魔法，
 * 也不可能再去和棋盘抢空间。
 */
export function ChatPanel({
  messages,
  disabled = false,
  taunts = [],
  onSend,
}: ChatPanelProps): ReactElement {
  const [tauntOpen, setTauntOpen] = useState(false)
  const [draft, setDraft] = useState('')
  const listRef = useRef<HTMLDivElement | null>(null)
  const tauntWrapRef = useRef<HTMLDivElement | null>(null)

  /**
   * 快捷台词菜单必须能「点外部 / 按 Escape」关掉。
   * 旧版只能靠选中某句台词或发送消息来关闭，菜单会一直悬在消息列表和棋盘上方，
   * 看上去就像布局错位、内容重叠 —— 这是可访问性要求，不只是观感问题。
   */
  useEffect(() => {
    if (!tauntOpen) return
    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target as Node | null
      if (target && tauntWrapRef.current?.contains(target)) return
      setTauntOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setTauntOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [tauntOpen])

  useEffect(() => {
    // 跟随最新消息滚动到底部，只能直接操作 DOM（React 无声明式替代）
    const node = listRef.current
    if (node) node.scrollTop = node.scrollHeight
  }, [messages.length])

  const submit = () => {
    const text = draft.trim()
    if (!text || disabled) return
    onSend(text)
    setDraft('')
    setTauntOpen(false)
  }

  const sendTaunt = (phrase: string) => {
    if (disabled) return
    onSend(phrase)
    setTauntOpen(false)
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col p-3">
      <div className="flex min-h-0 flex-1 flex-col">
        <ScrollArea
          viewportRef={listRef}
          viewportClassName="flex flex-col gap-1.5"
          role="log"
          ariaLive="polite"
          className="mb-2 min-h-0 flex-1"
        >
          {messages.map((entry) =>
            entry.system ? (
              <div key={entry.id} className="self-center text-xs text-white/50">
                {entry.text}
              </div>
            ) : (
              <div
                key={entry.id}
                className={`max-w-[80%] rounded-xl border px-2.5 py-1.5 text-[0.92rem] leading-tight [overflow-wrap:anywhere] ${entry.mine
                  ? 'self-end border-accent/50 bg-accent/25'
                  : 'self-start border-white/15 bg-white/10'
                  }`}
              >
                <span className="mb-0.5 block text-[0.72rem] opacity-60">
                  {entry.name ?? (entry.mine ? '我' : '对方')}
                </span>
                {entry.text}
              </div>
            ),
          )}
        </ScrollArea>

        <div className="flex items-stretch gap-2">
          {taunts.length === 0 ? null : (
            <div ref={tauntWrapRef} className="relative flex-none">
              <button
                type="button"
                className="h-full rounded-lg border border-warning/50 px-2.5 text-sm text-warning hover:bg-warning/10"
                title="快捷台词"
                aria-haspopup="menu"
                aria-expanded={tauntOpen}
                disabled={disabled}
                onClick={() => setTauntOpen((value) => !value)}
              >
                💬
              </button>
              {tauntOpen ? (
                // 定位交给外层包裹：ScrollArea 自身必须是 relative（滑块靠它做定位基准）
                <div className="absolute bottom-[calc(100%+6px)] left-0 z-20">
                  <ScrollArea
                    role="menu"
                    viewportClassName="flex flex-col gap-1.5"
                    // 移动端上限提到 45vh/320px：菜单项为触控加高到 38px 后，220px 只能看到 5 条。
                    // 桌面端保持紧凑（指针精度高，不需要那么高的靶）。
                    className="max-h-[min(45vh,320px)] w-[max(220px,60vw)] max-w-[250px] rounded-[10px] border border-white/15 bg-[#23262c] p-2 shadow-[0_8px_24px_rgba(0,0,0,0.5)] md:max-h-[min(35vh,220px)]"
                  >
                    {taunts.map((phrase) => (
                      <button
                        key={phrase}
                        type="button"
                        role="menuitem"
                        className="w-full rounded-lg border border-danger/40 bg-danger/15 px-2.5 py-2 text-left text-sm text-[#ffb3a7] hover:bg-danger/30"
                        onClick={() => sendTaunt(phrase)}
                      >
                        {phrase}
                      </button>
                    ))}
                  </ScrollArea>
                </div>
              ) : null}
            </div>
          )}
          <input
            // text-base（16px）+ min-h-11（44px）：iOS Safari 在聚焦小于 16px 的输入框时会自动放大页面，
            // 且 44px 才达得到推荐触控尺寸；桌面端用 md: 还原紧凑外观。
            className="min-h-11 min-w-0 flex-1 rounded-lg border border-white/20 bg-black/30 px-3 py-2 text-base text-white placeholder:text-white/40 md:min-h-0 md:text-sm"
            placeholder="说点什么…"
            maxLength={MAX_CHAT_LENGTH}
            autoComplete="off"
            value={draft}
            disabled={disabled}
            aria-label="聊天输入框"
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                submit()
              }
            }}
          />
          <button
            type="button"
            className="shrink-0 rounded-lg border border-white/25 px-3 py-2 text-sm text-white/90 hover:bg-white/10 disabled:opacity-50"
            disabled={disabled}
            onClick={submit}
          >
            发送
          </button>
        </div>
      </div>
    </div>
  )
}
